/**
 * A person's picture as the web app draws one (7g): their profile picture
 * where they have one, and otherwise their initials on a colour of their own.
 * Initials come from the name they gave, else their username — or a guest's
 * name, which is all a guest has. The colour follows who they are, so the
 * same person is the same colour on every page.
 */

/** Whom a picture is of, as every wire type that names a person sends it. */
export interface Person {
  id?: string;
  name: string | null;
  username?: string | null;
  /** The address of their profile picture, or null for initials. */
  image?: string | null;
}

/** The first letter or digit of a word, past any punctuation before it. */
function firstOf(word: string): string {
  return Array.from(word.replace(/^[^\p{L}\p{N}]+/u, ""))[0] ?? "";
}

/**
 * "Ana Lúcia Sousa" is AS, "ana" is A, an unnamed @bo is B, and somebody with
 * nothing to go by is "?".
 */
export function initials(name: string | null | undefined, username?: string | null): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length > 0) {
    const letters = firstOf(words[0]!) + (words.length > 1 ? firstOf(words.at(-1)!) : "");
    if (letters) return letters.toLocaleUpperCase();
  }
  const handle = firstOf((username ?? "").replace(/^@/, ""));
  return handle ? handle.toLocaleUpperCase() : "?";
}

/** The colours initials sit on, told apart: a soft fill and dark letters by day, the reverse by night. */
export const TONES = [
  "bg-[#dbe7ff] text-[#23384f] dark:bg-[#23384f] dark:text-[#cfe0ff]",
  "bg-[#d3f2de] text-[#24432f] dark:bg-[#24432f] dark:text-[#c9f2d6]",
  "bg-[#f4edb8] text-[#43401f] dark:bg-[#43401f] dark:text-[#f4edb8]",
  "bg-[#ffe1cf] text-[#4a3324] dark:bg-[#4a3324] dark:text-[#ffd9c2]",
  "bg-[#ece0fb] text-[#3b2f4a] dark:bg-[#3b2f4a] dark:text-[#e6d4fa]",
  "bg-[#cdf1f0] text-[#1f4243] dark:bg-[#1f4243] dark:text-[#c4f1f0]",
] as const;

/** One of the tones, always the same for the same key. */
export function toneOf(key: string): string {
  let hash = 0;
  for (const char of key) hash = (hash * 31 + char.codePointAt(0)!) >>> 0;
  return TONES[hash % TONES.length]!;
}
